import { gql } from "@apollo/client";

export const JOB_BASE_FIELDS = gql`
  fragment JobBaseFields on Job {
    _id
    active
    createdAt
    createdBy {
      _id
      username
      email
    }
    assignedTo {
      _id
      username
      email
    }
    meetingSnapshot {
      eventId
      title
      description
      startDateTime
      endDateTime
    }
    requirements
    requirementBadges {
      name
      icon
    }
    description
    applicationCount
  }
`
// JOB Info
export const QUERY_JOBS = gql`
  query Jobs($showAll: Boolean) {
    jobs(showAll: $showAll) {
      ...JobBaseFields
    }
  }
    ${JOB_BASE_FIELDS}
`;

export const QUERY_JOB = gql`
  query Job($id: ID!) {
    job(_id: $id) {
      ...JobBaseFields
      applications {
        _id
        appliedAt
        user {
          _id
          username
          email
        }
      }
    }
  }
  ${JOB_BASE_FIELDS}
`;

export const QUERY_ALL_JOBS = gql`
  query JobsAllForReport{
    jobs(showAll: true) {
      ...JobBaseFields
    }
  }
    ${JOB_BASE_FIELDS}
`;

export const QUERY_REPORT_JOBS = gql`
  query JobsReport($offset: Int, $limit: Int) {
    jobs(showAll: true, offset: $offset, limit: $limit) {
      _id
      active
      createdAt
      applicationCount
      createdBy {
        _id
        username
      }
      assignedTo {
        _id
        username
      }
      meetingSnapshot {
        eventId
        title
        startDateTime
      }
    }
  }
`;

export const QUERY_REPORT_JOBS_LEGACY = gql`
  query JobsReportLegacy {
    jobs(showAll: true) {
      _id
      active
      createdAt
      applicationCount
      createdBy {
        _id
        username
      }
      assignedTo {
        _id
        username
      }
      meetingSnapshot {
        eventId
        title
        startDateTime
      }
    }
  }
`;

// 1) Data query for the stats
export const QUERY_USER_JOB_STATS = gql`
  query GetUserJobStats {
    userJobStats {
      _id
      username
      createdCount
      assignedCount
      appliedCount
      totalMeetingsHosted
    }
  }
`;

// 2) Generic schema introspection for the UserJobStats type
export const QUERY_USER_JOB_STATS_SCHEMA = gql`
  query GetUserJobStatsSchema {
    __type(name: "UserJobStats") {
      name
      fields {
        name
        type {
          name
          kind
          ofType {
            name
            kind
          }
        }
      }
    }
  }
`;

export const QUERY_JOB_METRICS_OVER_TIME = gql`
  query GetJobMetricsOverTime($days: Int) {
    jobMetricsOverTime(days: $days) {
      date
      jobsPosted
      jobsAssigned
      totalApplications
    }
  }
`;

export const QUERY_MEETING_METRIC_OPTIONS = gql`
  query MeetingMetricOptions($days: Int) {
    meetingMetricOptions(days: $days) {
      meetingKey
      meetingTitle
      totalSubRequests
      totalAssigned
      averageApplicants
      latestStartDateTime
    }
  }
`;

export const QUERY_MEETING_METRICS_SUMMARY = gql`
  query MeetingMetricsSummary($meetingKey: String!, $days: Int) {
    meetingMetricsSummary(meetingKey: $meetingKey, days: $days) {
      meetingKey
      meetingTitle
      totalSubRequests
      totalApplicants
      averageApplicants
      totalAssigned
      uniqueSubs
      neverAssignedApplicants
      peerParentRanking {
        userId
        userName
        appliedCount
        subCount
      }
      instances {
        instanceKey
        startDateTime
        endDateTime
        totalSubRequests
        totalApplicants
        averageApplicants
        totalAssigned
        assignedToNames
        submittedByNames
      }
    }
  }
`;

export const QUERY_MEETING_INSTANCE_APPLICANTS = gql`
  query MeetingInstanceApplicants($meetingKey: String!, $instanceKey: String!, $days: Int) {
    meetingInstanceApplicants(meetingKey: $meetingKey, instanceKey: $instanceKey, days: $days) {
      jobId
      applicationId
      userId
      userName
      appliedAt
      assigned
      matchScore
      matched
      total
      constraintScore
      meetingsHosted
      workloadScore
      recentSubJobs
      recentSubScore
      totalSubJobs
      totalSubScore
      unassignedApplicationJobs
      unassignedApplicationScore
      applicationTimingScore
      scoringWeights {
        constraint
        meetingsHosted
        recentSubs
        totalSubs
        unassignedApplications
        applicationTiming
      }
      timingSettings {
        relativeWeight
        halfLifeHours
      }
      scoreSource
    }
  }
`;

export const QUERY_MATCH_ENGINE_DRY_RUN = gql`
  query MatchEngineDryRun($meetingId: ID!) {
    matchEngineDryRun(meetingId: $meetingId) {
      meetingId
      jobId
      meetingTitle
      constraintCount
      message
      scoringWeights {
        constraint
        meetingsHosted
        recentSubs
        totalSubs
        unassignedApplications
        applicationTiming
      }
      workloadBalanceWindowDays
      constraints {
        _id
        name
        fieldKey
        operator
        value
        required
      }
      applicants {
        applicationId
        userId
        userName
        isApplicant
        eligible
        matched
        total
        score
        constraintScore
        meetingsHosted
        workloadScore
        totalSubJobs
        totalSubScore
        unassignedApplicationJobs
        unassignedApplicationScore
        recentSubJobs
        recentSubScore
        applicationTimingScore
        appliedAt
        matchedConstraints
      }
    }
  }
`;

export const QUERY_MATCH_ENGINE_JOB_DRY_RUN = gql`
  query MatchEngineJobDryRun($jobId: ID!) {
    matchEngineJobDryRun(jobId: $jobId) {
      meetingId
      jobId
      meetingTitle
      constraintCount
      message
      scoringWeights {
        constraint
        meetingsHosted
        recentSubs
        totalSubs
        unassignedApplications
        applicationTiming
      }
      workloadBalanceWindowDays
      constraints {
        _id
        name
        fieldKey
        operator
        value
        required
      }
      applicants {
        applicationId
        userId
        userName
        isApplicant
        eligible
        matched
        total
        score
        constraintScore
        meetingsHosted
        workloadScore
        totalSubJobs
        totalSubScore
        unassignedApplicationJobs
        unassignedApplicationScore
        recentSubJobs
        recentSubScore
        applicationTimingScore
        appliedAt
        matchedConstraints
      }
    }
  }
`;


export const QUERY_ELIGIBLE_JOBS_FOR_MATCH_ENGINE = gql`
  query EligibleJobsForMatchEngine {
    eligibleJobsForMatchEngine {
      jobId
      meetingTitle
      startDateTime
      createdBy
      applicationCount
      meetingIsPast
      meetingIsTooFarFuture
      isEligible
    }
  }
`;

